// The Tonnetz: pitch classes on a triangular lattice (right = fifths, up-right
// = major thirds), every triangle a triad — upward triangles major, downward
// minor. P, L and R are the three EDGE-FLIPS of the current triangle: reflect
// across an edge and exactly one voice moves. Click triangles or use the
// buttons to walk; the trail draws your path through triad space.

import { useMemo, useState } from 'react';
import { spellNote, type PitchClass } from '@/music/notes';
import {
  hexatonicPole,
  tonnetzMove,
  tonnetzPc,
  tonnetzTriad,
  triadName,
  triadPcs,
  type PLRMove,
  type TonnetzPos as Pos,
  type Triad,
} from '@/music/plr';
import { getChordPlayer } from '@/audio/player';

// Visible lattice patch: 13 × 6 nodes.
const NI = 13;
const NJ = 6;
const W = 56; // fifth step (x)
const H = 52; // third step (row height)
const PAD = 26;

const pcAt = tonnetzPc;
const triadAt = tonnetzTriad;
const movePos = tonnetzMove;
const nx = (i: number, j: number) => PAD + i * W + j * (W / 2);
const ny = (j: number) => PAD + (NJ - 1 - j) * H;

/** Corner nodes of a triangle, as (i,j) pairs. */
function corners(pos: Pos): [number, number][] {
  return pos.up
    ? [
        [pos.i, pos.j],
        [pos.i + 1, pos.j],
        [pos.i, pos.j + 1],
      ]
    : [
        [pos.i + 1, pos.j],
        [pos.i, pos.j + 1],
        [pos.i + 1, pos.j + 1],
      ];
}

const inBounds = (p: Pos) => p.i >= 0 && p.j >= 0 && p.i + 1 < NI && p.j + 1 < NJ;

/** The in-bounds triangle spelling the same triad, nearest the patch centre. */
function recenter(t: Triad): Pos {
  const want = t.quality === 'major' ? t.rootPc : (((t.rootPc - 4) % 12) + 12) % 12;
  const up = t.quality === 'major';
  let best: Pos | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < NI - 1; i++) {
    for (let j = 0; j < NJ - 1; j++) {
      if (pcAt(i, j) !== want) continue;
      const d = Math.abs(i - (NI - 1) / 2) + Math.abs(j - (NJ - 1) / 2);
      if (d < bestDist) {
        bestDist = d;
        best = { i, j, up };
      }
    }
  }
  return best ?? { i: 4, j: 2, up };
}

const START: Pos = recenter({ rootPc: 0, quality: 'major' }); // C major

function centroid(pos: Pos): [number, number] {
  const cs = corners(pos);
  const xs = cs.map(([i, j]) => nx(i, j));
  const ys = cs.map(([, j]) => ny(j));
  return [xs.reduce((a, b) => a + b) / 3, ys.reduce((a, b) => a + b) / 3];
}

const spell = (pc: PitchClass) => spellNote(pc, 'flat');

function playTriad(t: Triad) {
  const [r, third, fifth] = triadPcs(t);
  const root = 48 + r;
  const up = (pc: number) => root + ((((pc - r) % 12) + 12) % 12);
  void getChordPlayer().playMidi([root, up(third), up(fifth)], 0.03);
}

export function TonnetzExhibit() {
  const [pos, setPos] = useState<Pos>(START);
  const [trail, setTrail] = useState<Pos[]>([START]);

  const triad = triadAt(pos);
  const pole = hexatonicPole(triad);
  const polePos = useMemo(() => recenter(pole), [pole]);

  const goto = (next: Pos) => {
    const target = inBounds(next) ? next : recenter(triadAt(next));
    setPos(target);
    setTrail((tr) => [...tr.slice(-30), target]);
    playTriad(triadAt(target));
  };
  const move = (m: PLRMove) => goto(movePos(pos, m));
  const jumpPole = () => goto(polePos);
  const reset = () => {
    setPos(START);
    setTrail([START]);
    playTriad(triadAt(START));
  };

  // All triangles of the patch.
  const triangles: Pos[] = useMemo(() => {
    const out: Pos[] = [];
    for (let i = 0; i < NI - 1; i++) {
      for (let j = 0; j < NJ - 1; j++) {
        out.push({ i, j, up: true }, { i, j, up: false });
      }
    }
    return out;
  }, []);

  const width = PAD * 2 + (NI - 1) * W + (NJ - 1) * (W / 2);
  const height = PAD * 2 + (NJ - 1) * H;
  const key = (p: Pos) => `${p.i},${p.j},${p.up}`;
  const activePcs = new Set(triadPcs(triad));

  return (
    <div className="lab-exhibit" data-testid="lab-tonnetz">
      <p className="lab-lede">
        Moving right is a perfect fifth and moving up-right a major third, so every triangle is a
        triad (▲&nbsp;major, ▽&nbsp;minor) and neighbouring triangles share two notes.{' '}
        <strong>P</strong>, <strong>L</strong> and <strong>R</strong> flip a triangle across one
        edge: one note moves and two stay. These moves reach all 24 triads. Beethoven uses an L–R
        chain in the Ninth Symphony. The <em>hexatonic pole</em> (P·L·P) shares no notes with the
        starting triad.
      </p>

      <div className="lab-controls" data-testid="tonnetz-controls">
        <span className="tonnetz-current" data-testid="tonnetz-current">
          {triadName(triad, spell)}
        </span>
        <button
          className="btn btn-sm"
          onClick={() => move('P')}
          title="Parallel: the third moves a semitone (C ↔ Cm)"
        >
          P
        </button>
        <button
          className="btn btn-sm"
          onClick={() => move('L')}
          title="Leittonwechsel: the root moves down a semitone (C ↔ Em)"
        >
          L
        </button>
        <button
          className="btn btn-sm"
          onClick={() => move('R')}
          title="Relative: the fifth moves up a tone (C ↔ Am)"
        >
          R
        </button>
        <button
          className="btn btn-sm"
          onClick={jumpPole}
          title="Hexatonic pole (P·L·P): no common notes"
        >
          Pole: {triadName(pole, spell)}
        </button>
        <button className="btn btn-sm btn-ghost" onClick={reset}>
          Reset
        </button>
      </div>

      <div className="tonnetz-scroll" tabIndex={0} aria-label="Scrollable Tonnetz diagram">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="tonnetz"
          role="img"
          aria-label="Tonnetz lattice; use the P, L and R controls above to move between triads"
        >
          {triangles.map((p) => {
            const cs = corners(p);
            const pts = cs.map(([i, j]) => `${nx(i, j)},${ny(j)}`).join(' ');
            const isCurrent = key(p) === key(pos);
            const isPole = key(p) === key(polePos);
            return (
              <polygon
                key={key(p)}
                points={pts}
                fill={isCurrent ? 'var(--accent)' : p.up ? 'var(--accent-soft)' : 'transparent'}
                fillOpacity={isCurrent ? 0.55 : 0.5}
                stroke={isPole ? 'var(--danger)' : 'var(--hairline)'}
                strokeWidth={isPole ? 2 : 1}
                strokeDasharray={isPole ? '4 3' : undefined}
                onClick={() => goto(p)}
                style={{ cursor: 'pointer' }}
              >
                <title>{triadName(triadAt(p), spell)}</title>
              </polygon>
            );
          })}
          {trail.length > 1 && (
            <polyline
              points={trail.map((p) => centroid(p).join(',')).join(' ')}
              fill="none"
              stroke="var(--accent)"
              strokeWidth={2.4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.8}
            />
          )}
          {Array.from({ length: NI }, (_, i) =>
            Array.from({ length: NJ }, (_, j) => {
              const pc = pcAt(i, j);
              const active = activePcs.has(pc);
              return (
                <g key={`${i}-${j}`} pointerEvents="none">
                  <circle
                    cx={nx(i, j)}
                    cy={ny(j)}
                    r={active ? 11 : 9}
                    fill="var(--surface)"
                    stroke={active ? 'var(--accent)' : 'var(--hairline)'}
                    strokeWidth={active ? 2 : 1}
                  />
                  <text
                    x={nx(i, j)}
                    y={ny(j)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={10}
                    fontWeight={active ? 800 : 500}
                    fill={active ? 'var(--ink)' : 'var(--muted)'}
                  >
                    {spell(pc)}
                  </text>
                </g>
              );
            }),
          )}
        </svg>
      </div>

      <p className="muted" data-testid="tonnetz-trail">
        {trail
          .slice(-12)
          .map((p) => triadName(triadAt(p), spell))
          .join(' → ')}
      </p>
    </div>
  );
}
